import { useFrame, useThree } from '@react-three/fiber';
import { type RefObject, useEffect, useRef } from 'react';
import { Camera, Vector3 } from 'three';
import { useShallow } from 'zustand/shallow';

import { usePlacedArticleMetrics } from 'app/modules/wallforming/components/content-container/hooks/usePlacedArticleMetrics';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';

import type { HeatmapColorMap } from 'app/modules/wallforming/components/content-container/heatmap/types/heatmapColorMap';
import type { HeatmapMetric } from 'app/modules/wallforming/components/content-container/heatmap/types/heatmapMetric';
import type { ArticleInstance } from 'app/modules/wallforming/components/scenes/objects/articles/types/articleInstance';

interface WeightedPoint {
    articleNumber: string;
    articleInstanceId: string;
    position: Vector3;
    weight: number;
    label: string;
    ppmCode: string | null;
    price: number;
    co2: number;
    ppmScore: number;
    ppmPenaltyNorm: number;
    priceNorm: number;
    co2Norm: number;
    thumbnailUri: string | null;
    articleName: string;
}

const tmpVector = new Vector3();

interface RawMetricPoint {
    articleNumber: string;
    articleInstanceId: string;
    position: Vector3;
    price: number;
    co2: number;
    ppmCode: string | null;
    ppmScore: number;
    ppmPenalty: number;
    hasPpmPenalty: boolean;
}

interface ProjectedPoint {
    x: number;
    y: number;
    radius: number;
    point: WeightedPoint;
}

interface HeatFieldGrid {
    cols: number;
    rows: number;
    cellSize: number;
    field: Float32Array;
}

interface HeatmapRaster {
    canvas: HTMLCanvasElement;
    context: CanvasRenderingContext2D;
    imageData: ImageData;
}

interface ViewportSize {
    width: number;
    height: number;
}

interface LabelCluster {
    x: number;
    y: number;
    radius: number;
    count: number;
    ppmCode: string | null;
    totalWeight: number;
    totalPrice: number;
    totalCo2: number;
    totalPpmScore: number;
}

interface HeatmapPanelTheme {
    panelBackground: string;
    panelBorder: string;
    panelShadow: string;
    textPrimary: string;
    textMuted: string;
}

const getCssVar = (style: CSSStyleDeclaration, name: string, fallback: string): string => {
    const value = style.getPropertyValue(name).trim();
    return value.length > 0 ? value : fallback;
};

const getHeatmapPanelTheme = (): HeatmapPanelTheme => {
    const style = getComputedStyle(document.documentElement);
    return {
        panelBackground: getCssVar(style, '--color-background-default', 'rgba(255, 255, 255, 0.96)'),
        panelBorder: getCssVar(style, '--color-border-default', 'rgba(24, 36, 58, 0.25)'),
        panelShadow: 'rgba(8, 16, 32, 0.22)',
        textPrimary: getCssVar(style, '--color-text-default', '#1f2937'),
        textMuted: getCssVar(style, '--color-text-muted', '#64748b')
    };
};

const formatValueLabel = (
    metric: HeatmapMetric,
    price: number,
    co2: number,
    ppmCode: string | null,
    overallWeight: number
): string => {
    switch (metric) {
        case 'price':
            return `${Math.round(price)}`;
        case 'co2':
            return `${co2.toFixed(1)} kg`;
        case 'ppm':
            return ppmCode ?? 'N/A';
        case 'overall':
        default:
            return `${Math.round(clamp01(overallWeight) * 100)}%`;
    }
};

const getPpmBadgeColors = (ppmCode: string | null): { background: string; border: string; text: string } => {
    switch (ppmCode) {
        case 'G':
            return { background: '#2a9f7b', border: 'rgba(255, 255, 255, 0.7)', text: '#ffffff' };
        case 'P':
            return { background: '#7f3470', border: 'rgba(255, 255, 255, 0.7)', text: '#ffffff' };
        case 'I':
            return { background: '#a6d61f', border: 'rgba(255, 255, 255, 0.7)', text: '#ffffff' };
        case 'O':
            return { background: '#7f7f7f', border: 'rgba(255, 255, 255, 0.7)', text: '#ffffff' };
        case 'E':
            return { background: '#b7b7b7', border: 'rgba(255, 255, 255, 0.7)', text: '#ffffff' };
        case 'N':
            return { background: '#d6d6d6', border: 'rgba(255, 255, 255, 0.7)', text: '#ffffff' };
        case 'NA':
            return { background: '#dddddd', border: 'rgba(255, 255, 255, 0.7)', text: '#ffffff' };
        default:
            return { background: '#6b7280', border: 'rgba(255, 255, 255, 0.65)', text: '#ffffff' };
    }
};

const ppmCodeSeverity: Record<string, number> = {
    G: 1,
    P: 2,
    I: 3,
    O: 4,
    E: 5,
    N: 6,
    NA: 7
};

const pickWorsePpmCode = (left: string | null, right: string | null): string | null => {
    if (!left) {
        return right;
    }
    if (!right) {
        return left;
    }

    return (ppmCodeSeverity[right] ?? 0) > (ppmCodeSeverity[left] ?? 0) ? right : left;
};

const formatClusterLabel = (metric: HeatmapMetric, cluster: LabelCluster): string => {
    const aggregationPrefix = cluster.count > 1 ? `Σ(${cluster.count}) ` : '';

    switch (metric) {
        case 'price':
            return `${aggregationPrefix}${Math.round(cluster.totalPrice)}`;
        case 'co2':
            return `${aggregationPrefix}${cluster.totalCo2.toFixed(1)} kg`;
        case 'ppm':
            return `${aggregationPrefix}${cluster.ppmCode ?? 'N/A'}`;
        case 'overall':
        default:
            return `${aggregationPrefix}${Math.round(cluster.totalWeight * 100)}%`;
    }
};

const clusterProjectedLabels = (projectedPoints: ProjectedPoint[]): LabelCluster[] => {
    const clusters: LabelCluster[] = [];
    const mergeDistance = 14;
    const mergeDistanceSq = mergeDistance * mergeDistance;

    for (let i = 0; i < projectedPoints.length; i += 1) {
        const projected = projectedPoints[i];
        let matchingCluster: LabelCluster | undefined;

        for (let j = 0; j < clusters.length; j += 1) {
            const cluster = clusters[j];
            const dx = projected.x - cluster.x;
            const dy = projected.y - cluster.y;
            if (dx * dx + dy * dy <= mergeDistanceSq) {
                matchingCluster = cluster;
                break;
            }
        }

        if (!matchingCluster) {
            clusters.push({
                x: projected.x,
                y: projected.y,
                radius: projected.radius,
                count: 1,
                ppmCode: projected.point.ppmCode,
                totalWeight: projected.point.weight,
                totalPrice: projected.point.price,
                totalCo2: projected.point.co2,
                totalPpmScore: projected.point.ppmScore
            });
            continue;
        }

        const nextCount = matchingCluster.count + 1;
        matchingCluster.x = (matchingCluster.x * matchingCluster.count + projected.x) / nextCount;
        matchingCluster.y = (matchingCluster.y * matchingCluster.count + projected.y) / nextCount;
        matchingCluster.radius = Math.max(matchingCluster.radius, projected.radius);
        matchingCluster.count = nextCount;
        matchingCluster.ppmCode = pickWorsePpmCode(matchingCluster.ppmCode, projected.point.ppmCode);
        matchingCluster.totalWeight += projected.point.weight;
        matchingCluster.totalPrice += projected.point.price;
        matchingCluster.totalCo2 += projected.point.co2;
        matchingCluster.totalPpmScore += projected.point.ppmScore;
    }

    return clusters;
};

const getMetricDescription = (metric: HeatmapMetric): string => {
    switch (metric) {
        case 'price':
            return 'Hot = higher price';
        case 'co2':
            return 'Hot = higher CO2';
        case 'ppm':
            return 'Hot = worse lifecycle';
        case 'overall':
        default:
            return 'Hot = worse combined score';
    }
};

const getInstanceInsertionPoint = (instance: ArticleInstance): Vector3 | null => {
    const partKeys = Object.keys(instance.parts);
    if (partKeys.length === 0) {
        return null;
    }
    const anchorPartKey = partKeys.length === 1 ? partKeys[0] : (partKeys.find((key) => key.length === 8) ?? partKeys[0]);
    return instance.parts[anchorPartKey]?.position ?? null;
};

const normalize01 = (value: number, min: number, max: number): number => {
    if (max <= min) {
        return 0.5;
    }

    return clamp01((value - min) / (max - min));
};

const getPointWeight = (
    metric: HeatmapMetric,
    priceNorm: number,
    co2Norm: number,
    ppmPenaltyNorm: number,
    hasPpmPenalty: boolean
): number => {

    switch (metric) {
        case 'price':
            return clamp01(priceNorm);
        case 'co2':
            return clamp01(co2Norm);
        case 'ppm':
            return hasPpmPenalty ? clamp01(ppmPenaltyNorm) : 0.5;
        case 'overall':
        default:
            if (!hasPpmPenalty) {
                return (clamp01(priceNorm) + clamp01(co2Norm)) / 2;
            }

            return (clamp01(priceNorm) + clamp01(co2Norm) + clamp01(ppmPenaltyNorm)) / 3;
    }
};

const clamp01 = (value: number): number => Math.min(1, Math.max(0, value));

const lerp = (from: number, to: number, amount: number): number => from + (to - from) * amount;

const getClassicHeatRgb = (weight: number): { r: number; g: number; b: number } => {
    const t = clamp01(weight);
    const stops = [
        { t: 0, rgb: [24, 48, 120] as const },
        { t: 0.2, rgb: [34, 110, 255] as const },
        { t: 0.42, rgb: [0, 210, 255] as const },
        { t: 0.6, rgb: [70, 220, 120] as const },
        { t: 0.78, rgb: [255, 220, 64] as const },
        { t: 0.9, rgb: [255, 120, 24] as const },
        { t: 1, rgb: [220, 32, 32] as const }
    ];

    for (let i = 0; i < stops.length - 1; i += 1) {
        const left = stops[i];
        const right = stops[i + 1];
        if (t <= right.t) {
            const local = (t - left.t) / (right.t - left.t || 1);
            return {
                r: Math.round(lerp(left.rgb[0], right.rgb[0], local)),
                g: Math.round(lerp(left.rgb[1], right.rgb[1], local)),
                b: Math.round(lerp(left.rgb[2], right.rgb[2], local))
            };
        }
    }

    return { r: 220, g: 32, b: 32 };
};

const getHeatRgb = (weight: number, colorMap: HeatmapColorMap): { r: number; g: number; b: number } => {
    const t = clamp01(weight);
    switch (colorMap) {
        case 'classic': {
            return getClassicHeatRgb(t);
        }
        case 'greenRed': {
            const r = Math.floor(255 * t);
            const g = Math.floor(255 * (1 - t));
            return { r, g, b: 0 };
        }
        case 'blueRed': {
            const r = Math.floor(255 * t);
            const b = Math.floor(255 * (1 - t));
            return { r, g: 0, b };
        }
        case 'heat': {
            // Black → Red → Yellow → White
            const r = Math.floor(Math.min(255, 510 * t));
            const g = Math.floor(Math.max(0, Math.min(255, 510 * (t - 0.5))));
            const b = Math.floor(Math.max(0, Math.min(255, 510 * (t - 0.75))));
            return { r, g, b };
        }
        case 'mono': {
            const v = Math.floor(255 * t);
            return { r: v, g: v, b: v };
        }
        case 'redIntensity': {
            // Single hue red map: heat is encoded by alpha/intensity only.
            return { r: 255, g: 0, b: 0 };
        }
        case 'original': {
            const r = Math.floor(255 * t);
            const g = Math.floor(190 * (1 - Math.abs(t - 0.5) * 2) + 40);
            const b = Math.floor(255 * (1 - t));
            return { r, g, b };
        }
        default:
            return { r: 255, g: 0, b: 0 };
    }
};

const getHeatAlpha = (weight: number, colorMap: HeatmapColorMap): number => {
    const t = clamp01(weight);
    if (colorMap === 'redIntensity') {
        return 0.12 + t * 0.78;
    }

    if (colorMap === 'classic') {
        return 0.24 + t * 0.56;
    }

    return 0.18 + t * 0.62;
};

const getHeatmapCellSize = (canvasWidth: number, canvasHeight: number): number => {
    return Math.max(18, Math.min(24, Math.round(Math.min(canvasWidth, canvasHeight) / 40)));
};

const buildHeatFieldGrid = (projectedPoints: ProjectedPoint[], canvasWidth: number, canvasHeight: number): HeatFieldGrid => {
    const cellSize = getHeatmapCellSize(canvasWidth, canvasHeight);
    const cols = Math.max(1, Math.ceil(canvasWidth / cellSize));
    const rows = Math.max(1, Math.ceil(canvasHeight / cellSize));
    const field = new Float32Array(cols * rows);

    for (let i = 0; i < projectedPoints.length; i += 1) {
        const projected = projectedPoints[i];
        const influenceRadius = Math.max(cellSize * 2.4, 42 + projected.point.weight * 110);
        const radiusSq = influenceRadius * influenceRadius;
        const minCol = Math.max(0, Math.floor((projected.x - influenceRadius) / cellSize));
        const maxCol = Math.min(cols - 1, Math.floor((projected.x + influenceRadius) / cellSize));
        const minRow = Math.max(0, Math.floor((projected.y - influenceRadius) / cellSize));
        const maxRow = Math.min(rows - 1, Math.floor((projected.y + influenceRadius) / cellSize));

        for (let row = minRow; row <= maxRow; row += 1) {
            const centerY = row * cellSize + cellSize / 2;

            for (let col = minCol; col <= maxCol; col += 1) {
                const centerX = col * cellSize + cellSize / 2;
                const dx = centerX - projected.x;
                const dy = centerY - projected.y;
                const distSq = dx * dx + dy * dy;
                if (distSq > radiusSq) {
                    continue;
                }

                const distance = Math.sqrt(distSq);
                const falloff = 1 - distance / influenceRadius;
                const contribution = projected.point.weight * falloff * falloff;
                const index = row * cols + col;
                field[index] = Math.min(1, field[index] + contribution);
            }
        }
    }

    return { cols, rows, cellSize, field };
};

const getSmoothedFieldValue = (grid: HeatFieldGrid, col: number, row: number): number => {
    let total = 0;
    let totalWeight = 0;

    for (let rowOffset = -1; rowOffset <= 1; rowOffset += 1) {
        const nextRow = row + rowOffset;
        if (nextRow < 0 || nextRow >= grid.rows) {
            continue;
        }

        for (let colOffset = -1; colOffset <= 1; colOffset += 1) {
            const nextCol = col + colOffset;
            if (nextCol < 0 || nextCol >= grid.cols) {
                continue;
            }

            const weight = rowOffset === 0 && colOffset === 0 ? 4 : rowOffset === 0 || colOffset === 0 ? 2 : 1;
            total += grid.field[nextRow * grid.cols + nextCol] * weight;
            totalWeight += weight;
        }
    }

    return totalWeight > 0 ? total / totalWeight : 0;
};

const getHeatmapRaster = (cols: number, rows: number): HeatmapRaster | null => {
    const rasterCanvas = document.createElement('canvas');
    rasterCanvas.width = cols;
    rasterCanvas.height = rows;
    const rasterContext = rasterCanvas.getContext('2d', { willReadFrequently: true });
    if (!rasterContext) {
        return null;
    }

    return {
        canvas: rasterCanvas,
        context: rasterContext,
        imageData: rasterContext.createImageData(cols, rows)
    };
};

const copyMatrixElements = (target: number[], source: ArrayLike<number>): void => {
    for (let i = 0; i < source.length; i += 1) {
        target[i] = source[i];
    }
};

const matrixElementsChanged = (cached: number[], next: ArrayLike<number>): boolean => {
    if (cached.length !== next.length) {
        return true;
    }

    for (let i = 0; i < next.length; i += 1) {
        if (cached[i] !== next[i]) {
            return true;
        }
    }

    return false;
};

interface HeatmapRendererProps {
    canvasRef: RefObject<HTMLCanvasElement | null>;
}

/**
 * Pure R3F component (renders nothing to DOM).
 * Draws the heatmap onto the external canvas ref every frame using camera projection.
 */
export const HeatmapRenderer = ({ canvasRef }: HeatmapRendererProps) => {
    const camera = useThree((state) => state.camera);
    const gl = useThree((state) => state.gl);
    const invalidate = useThree((state) => state.invalidate);
    const { metricsByArticleNumber } = usePlacedArticleMetrics();
    const weightedPointsRef = useRef<WeightedPoint[]>([]);
    const projectedPointsRef = useRef<ProjectedPoint[]>([]);
    const labelClustersRef = useRef<LabelCluster[]>([]);
    const lastHoveredInstanceIdRef = useRef<string | null>(null);
    const heatmapRasterRef = useRef<HeatmapRaster | null>(null);
    const thumbnailCacheRef = useRef<Map<string, HTMLImageElement | null>>(new Map());
    const heatmapDirtyRef = useRef(true);
    const projectionDirtyRef = useRef(true);
    const viewportSizeRef = useRef<ViewportSize>({ width: 0, height: 0 });
    const cameraMatrixRef = useRef<number[]>([]);
    const projectionMatrixRef = useRef<number[]>([]);
    const hoveredInstanceSetRef = useRef<Set<string>>(new Set());

    const { articles, showHeatmapOverlay, showHeatmapLabels, showHeatmapHover, heatmapMetric, heatmapColorMap, hoveredArticleInstanceIds } = usePlacementStore(
        useShallow((state) => ({
            articles: state.articles,
            showHeatmapOverlay: state.showHeatmapOverlay,
            showHeatmapLabels: state.showHeatmapLabels,
            showHeatmapHover: state.showHeatmapHover,
            heatmapMetric: state.heatmapMetric,
            heatmapColorMap: state.heatmapColorMap,
            hoveredArticleInstanceIds: state.hoveredArticleInstanceIds
        }))
    );

    useEffect(() => {
        const state = usePlacementStore.getState();
        const rawMetricPoints: RawMetricPoint[] = [];

        Object.entries(articles).forEach(([articleId, article]) => {
            const metrics = metricsByArticleNumber.get(article.articleNumber);
            if (!metrics) {
                return;
            }

            const hasPpmPenalty = true;
            const ppmPenalty = 1 - clamp01(metrics.ppmScore / 100);

            Object.entries(article.articleInstances).forEach(([instanceId, instance]) => {
                const insertionPoint = state.computeArticleInsertionPoint(articleId, instance) ?? getInstanceInsertionPoint(instance);
                if (!insertionPoint) {
                    return;
                }

                rawMetricPoints.push({
                    articleNumber: article.articleNumber,
                    articleInstanceId: instanceId,
                    position: insertionPoint,
                    price: metrics.price,
                    co2: metrics.co2KgEquivalent,
                    ppmCode: metrics.ppmCode ?? null,
                    ppmScore: metrics.ppmScore,
                    ppmPenalty,
                    hasPpmPenalty
                });
            });
        });

        if (rawMetricPoints.length === 0) {
            weightedPointsRef.current = [];
            projectedPointsRef.current = [];
            labelClustersRef.current = [];
            heatmapDirtyRef.current = true;
            projectionDirtyRef.current = true;
            invalidate();
            return;
        }

        let minPrice = Number.POSITIVE_INFINITY;
        let maxPrice = Number.NEGATIVE_INFINITY;
        let minCo2 = Number.POSITIVE_INFINITY;
        let maxCo2 = Number.NEGATIVE_INFINITY;
        let minPpmPenalty = Number.POSITIVE_INFINITY;
        let maxPpmPenalty = Number.NEGATIVE_INFINITY;

        for (let i = 0; i < rawMetricPoints.length; i += 1) {
            const point = rawMetricPoints[i];
            minPrice = Math.min(minPrice, point.price);
            maxPrice = Math.max(maxPrice, point.price);
            minCo2 = Math.min(minCo2, point.co2);
            maxCo2 = Math.max(maxCo2, point.co2);
            if (point.hasPpmPenalty) {
                minPpmPenalty = Math.min(minPpmPenalty, point.ppmPenalty);
                maxPpmPenalty = Math.max(maxPpmPenalty, point.ppmPenalty);
            }
        }

        weightedPointsRef.current = rawMetricPoints.map((point) => {
            const priceNorm = normalize01(point.price, minPrice, maxPrice);
            const co2Norm = normalize01(point.co2, minCo2, maxCo2);
            const ppmPenaltyNorm = point.hasPpmPenalty
                ? normalize01(point.ppmPenalty, minPpmPenalty, maxPpmPenalty)
                : 0.5;
            const weight = getPointWeight(heatmapMetric, priceNorm, co2Norm, ppmPenaltyNorm, point.hasPpmPenalty);

            const metrics = metricsByArticleNumber.get(point.articleNumber);
            const thumbnailUri = metrics?.thumbnailUri ?? null;

            // Eagerly start loading thumbnail images into the cache so they are
            // ready when the hover panel needs to draw them.
            if (thumbnailUri && !thumbnailCacheRef.current.has(thumbnailUri)) {
                thumbnailCacheRef.current.set(thumbnailUri, null);
                const img = new Image();
                img.onload = () => {
                    thumbnailCacheRef.current.set(thumbnailUri, img);
                };
                img.src = thumbnailUri;
            }

            return {
                articleNumber: point.articleNumber,
                articleInstanceId: point.articleInstanceId,
                position: point.position,
                weight,
                label: formatValueLabel(heatmapMetric, point.price, point.co2, point.ppmCode, weight),
                ppmCode: point.ppmCode,
                price: point.price,
                co2: point.co2,
                ppmScore: point.ppmScore,
                ppmPenaltyNorm,
                priceNorm,
                co2Norm,
                thumbnailUri,
                articleName: point.articleNumber
            };
        });

        heatmapDirtyRef.current = true;
        projectionDirtyRef.current = true;
        invalidate();
    }, [articles, heatmapMetric, invalidate, metricsByArticleNumber]);

    useEffect(() => {
        heatmapDirtyRef.current = true;
        invalidate();
    }, [heatmapColorMap, invalidate]);

    useEffect(() => {
        hoveredInstanceSetRef.current = new Set(hoveredArticleInstanceIds);
        invalidate();
    }, [hoveredArticleInstanceIds, invalidate]);

    useEffect(() => {
        invalidate();
    }, [showHeatmapLabels, showHeatmapOverlay, showHeatmapHover, invalidate]);

    useFrame(() => {
        const canvas = canvasRef.current;
        if (!canvas) {
            return;
        }

        const context = canvas.getContext('2d');
        if (!context) {
            return;
        }

        const viewportWidth = gl.domElement.clientWidth;
        const viewportHeight = gl.domElement.clientHeight;

        if (canvas.width !== viewportWidth || canvas.height !== viewportHeight) {
            canvas.width = viewportWidth;
            canvas.height = viewportHeight;
        }

        const viewportChanged =
            viewportSizeRef.current.width !== canvas.width || viewportSizeRef.current.height !== canvas.height;

        if (viewportChanged) {
            viewportSizeRef.current = { width: canvas.width, height: canvas.height };
            projectionDirtyRef.current = true;
            heatmapDirtyRef.current = true;
        }

        context.clearRect(0, 0, canvas.width, canvas.height);

        const shouldRenderHeatmapContent = showHeatmapOverlay || showHeatmapLabels || showHeatmapHover;
        if (!shouldRenderHeatmapContent) {
            return;
        }

        if (canvas.width === 0 || canvas.height === 0) {
            return;
        }

        const weightedPoints = weightedPointsRef.current;
        if (weightedPoints.length === 0) {
            return;
        }

        const cameraMatrixChanged = matrixElementsChanged(cameraMatrixRef.current, camera.matrixWorld.elements);
        const projectionMatrixChanged = matrixElementsChanged(projectionMatrixRef.current, camera.projectionMatrix.elements);

        if (cameraMatrixChanged || projectionMatrixChanged) {
            copyMatrixElements(cameraMatrixRef.current, camera.matrixWorld.elements);
            copyMatrixElements(projectionMatrixRef.current, camera.projectionMatrix.elements);
            projectionDirtyRef.current = true;
            heatmapDirtyRef.current = true;
        }

        if (projectionDirtyRef.current) {
            const projectedPoints: ProjectedPoint[] = [];

            for (let i = 0; i < weightedPoints.length; i += 1) {
                const point = weightedPoints[i];
                tmpVector.copy(point.position).project(camera as Camera);

                if (tmpVector.z < -1 || tmpVector.z > 1) {
                    continue;
                }

                const x = (tmpVector.x * 0.5 + 0.5) * canvas.width;
                const y = (-tmpVector.y * 0.5 + 0.5) * canvas.height;
                const clampedWeight = clamp01(point.weight);

                const radius = 45 + clampedWeight * 130;
                projectedPoints.push({ x, y, radius, point });
            }

            projectedPointsRef.current = projectedPoints;
            labelClustersRef.current = clusterProjectedLabels(projectedPoints);
            projectionDirtyRef.current = false;
        }

        if (heatmapDirtyRef.current && showHeatmapOverlay) {
            const heatFieldGrid = buildHeatFieldGrid(projectedPointsRef.current, canvas.width, canvas.height);
            const needsNewRaster =
                !heatmapRasterRef.current ||
                heatmapRasterRef.current.canvas.width !== heatFieldGrid.cols ||
                heatmapRasterRef.current.canvas.height !== heatFieldGrid.rows;

            if (needsNewRaster) {
                heatmapRasterRef.current = getHeatmapRaster(heatFieldGrid.cols, heatFieldGrid.rows);
            }

            const heatmapRaster = heatmapRasterRef.current;
            if (heatmapRaster) {
                const pixels = heatmapRaster.imageData.data;

                for (let row = 0; row < heatFieldGrid.rows; row += 1) {
                    for (let col = 0; col < heatFieldGrid.cols; col += 1) {
                        const value = getSmoothedFieldValue(heatFieldGrid, col, row);
                        const color = getHeatRgb(value, heatmapColorMap);
                        const alpha = getHeatAlpha(value, heatmapColorMap);
                        const index = (row * heatFieldGrid.cols + col) * 4;

                        pixels[index] = color.r;
                        pixels[index + 1] = color.g;
                        pixels[index + 2] = color.b;
                        pixels[index + 3] = Math.round(alpha * 255);
                    }
                }

                heatmapRaster.context.putImageData(heatmapRaster.imageData, 0, 0);
            }

            heatmapDirtyRef.current = false;
        }

        const heatmapRaster = heatmapRasterRef.current;
        if (showHeatmapOverlay && heatmapRaster) {
            context.save();
            context.imageSmoothingEnabled = true;
            context.drawImage(heatmapRaster.canvas, 0, 0, canvas.width, canvas.height);
            context.restore();
        }

        if (showHeatmapLabels) {
            const labelClusters = labelClustersRef.current;

            for (let i = 0; i < labelClusters.length; i += 1) {
                const cluster = labelClusters[i];
                const label = formatClusterLabel(heatmapMetric, cluster);
                const paddingX = cluster.ppmCode ? 6 : 0;
                const paddingY = cluster.ppmCode ? 3 : 0;
                const badgeColors = getPpmBadgeColors(cluster.ppmCode);

                context.save();
                context.font = '600 12px sans-serif';
                context.textAlign = 'center';
                context.textBaseline = 'middle';
                const textWidth = context.measureText(label).width;
                const badgeWidth = textWidth + paddingX * 2;
                const badgeHeight = 16 + paddingY * 2;
                const labelOffset = Math.min(18, Math.max(8, cluster.radius * 0.14));
                const preferredLabelY = cluster.y - labelOffset;
                const labelY = Math.max(badgeHeight / 2 + 6, preferredLabelY);

                if (cluster.ppmCode) {
                    const rectX = cluster.x - badgeWidth / 2;
                    const rectY = labelY - badgeHeight / 2;
                    const badgeRadius = 4;

                    context.fillStyle = badgeColors.background;
                    context.strokeStyle = badgeColors.border;
                    context.lineWidth = 1;
                    context.beginPath();
                    context.moveTo(rectX + badgeRadius, rectY);
                    context.lineTo(rectX + badgeWidth - badgeRadius, rectY);
                    context.quadraticCurveTo(rectX + badgeWidth, rectY, rectX + badgeWidth, rectY + badgeRadius);
                    context.lineTo(rectX + badgeWidth, rectY + badgeHeight - badgeRadius);
                    context.quadraticCurveTo(rectX + badgeWidth, rectY + badgeHeight, rectX + badgeWidth - badgeRadius, rectY + badgeHeight);
                    context.lineTo(rectX + badgeRadius, rectY + badgeHeight);
                    context.quadraticCurveTo(rectX, rectY + badgeHeight, rectX, rectY + badgeHeight - badgeRadius);
                    context.lineTo(rectX, rectY + badgeRadius);
                    context.quadraticCurveTo(rectX, rectY, rectX + badgeRadius, rectY);
                    context.closePath();
                    context.fill();
                    context.stroke();

                    context.fillStyle = badgeColors.text;
                    context.fillText(label, cluster.x, labelY);
                } else {
                    context.strokeStyle = 'rgba(0, 0, 0, 0.55)';
                    context.lineWidth = 3;
                    context.strokeText(label, cluster.x, labelY);
                    context.fillStyle = 'rgba(255, 255, 255, 0.95)';
                    context.fillText(label, cluster.x, labelY);
                }
                context.restore();
            }
        }

        const panelTheme = showHeatmapOverlay || showHeatmapHover ? getHeatmapPanelTheme() : null;

        // Legend — only visible when the heat overlay is active
        if (!showHeatmapOverlay) {
            // skip legend
        } else {
        const legendWidth = 196;
        const legendHeight = 64;
        const legendX = canvas.width - legendWidth - 14;
        const legendBottomOffset = 56;
        const legendY = Math.max(14, canvas.height - legendHeight - legendBottomOffset);
        const legendGradientY = legendY + 30;

        context.save();
        context.shadowColor = panelTheme?.panelShadow ?? 'rgba(8, 16, 32, 0.22)';
        context.shadowBlur = 8;
        context.fillStyle = panelTheme?.panelBackground ?? 'rgba(255, 255, 255, 0.96)';
        context.strokeStyle = panelTheme?.panelBorder ?? 'rgba(24, 36, 58, 0.25)';
        context.lineWidth = 1;
        context.beginPath();
        context.roundRect(legendX, legendY, legendWidth, legendHeight, 8);
        context.fill();
        context.stroke();
        context.shadowBlur = 0;

        context.font = '600 11px sans-serif';
        context.fillStyle = panelTheme?.textPrimary ?? '#1f2937';
        context.textAlign = 'left';
        context.fillText(getMetricDescription(heatmapMetric), legendX + 10, legendY + 17);

        const gradient = context.createLinearGradient(legendX + 10, legendGradientY, legendX + legendWidth - 10, legendGradientY);
        gradient.addColorStop(0, `rgb(${getHeatRgb(0, heatmapColorMap).r}, ${getHeatRgb(0, heatmapColorMap).g}, ${getHeatRgb(0, heatmapColorMap).b})`);
        gradient.addColorStop(0.5, `rgb(${getHeatRgb(0.5, heatmapColorMap).r}, ${getHeatRgb(0.5, heatmapColorMap).g}, ${getHeatRgb(0.5, heatmapColorMap).b})`);
        gradient.addColorStop(1, `rgb(${getHeatRgb(1, heatmapColorMap).r}, ${getHeatRgb(1, heatmapColorMap).g}, ${getHeatRgb(1, heatmapColorMap).b})`);
        context.fillStyle = gradient;
        context.fillRect(legendX + 10, legendGradientY, legendWidth - 20, 10);

        context.font = '500 10px sans-serif';
        context.fillStyle = panelTheme?.textMuted ?? '#64748b';
        context.fillText('Low', legendX + 10, legendGradientY + 22);
        context.textAlign = 'right';
        context.fillText('High', legendX + legendWidth - 10, legendGradientY + 22);
        context.restore();
        } // end legend block

        // Hover details sourced from scene hover state only.
        if (!showHeatmapHover) {
            return;
        }
        const hoveredInstanceSet = hoveredInstanceSetRef.current;
        let hovered: ProjectedPoint | null = null;

        if (hoveredInstanceSet.size > 0) {
            const preferredHovered =
                lastHoveredInstanceIdRef.current && hoveredInstanceSet.has(lastHoveredInstanceIdRef.current)
                    ? projectedPointsRef.current.find(
                          (projected) => projected.point.articleInstanceId === lastHoveredInstanceIdRef.current
                      )
                    : undefined;

            if (preferredHovered) {
                hovered = preferredHovered;
            } else {
                hovered = projectedPointsRef.current.find((projected) =>
                    hoveredInstanceSet.has(projected.point.articleInstanceId)
                ) ?? null;
            }
        }

        if (hovered) {
            lastHoveredInstanceIdRef.current = hovered.point.articleInstanceId;

            const info = hovered.point;
            const lines = [
                `Price: ${Math.round(info.price)}`,
                `CO2: ${info.co2.toFixed(1)} kg`,
                `PPM: ${info.ppmCode ?? 'N/A'} (${Math.round(info.ppmScore)})`,
                `Contribution: ${Math.round(info.weight * 100)}%`
            ];

            const thumbnailImg = info.thumbnailUri ? (thumbnailCacheRef.current.get(info.thumbnailUri) ?? null) : null;
            const thumbSize = 56;
            const thumbPadding = 8;
            const hasThumb = thumbnailImg !== null;

            context.save();
            context.font = '500 11px sans-serif';
            const lineHeight = 14;
            const textBlockWidth = Math.max(
                context.measureText(info.articleName).width + 2,
                ...lines.map((line) => context.measureText(line).width)
            );
            const thumbBlockWidth = hasThumb ? thumbSize + thumbPadding : 0;
            const innerWidth = textBlockWidth + thumbBlockWidth;
            const boxPaddingX = 10;
            const boxPaddingY = 8;
            const titleHeight = lineHeight + 4;
            const boxWidth = innerWidth + boxPaddingX * 2;
            const boxHeight = titleHeight + lines.length * lineHeight + boxPaddingY * 2;
            const anchorX = hovered.x;
            const anchorY = hovered.y;
            const boxX = Math.min(canvas.width - boxWidth - 8, anchorX + 14);
            const boxY = Math.max(8, anchorY - boxHeight - 10);

            context.shadowColor = panelTheme?.panelShadow ?? 'rgba(8, 16, 32, 0.22)';
            context.shadowBlur = 8;
            context.fillStyle = panelTheme?.panelBackground ?? 'rgba(255, 255, 255, 0.96)';
            context.strokeStyle = panelTheme?.panelBorder ?? 'rgba(24, 36, 58, 0.25)';
            context.lineWidth = 1;
            context.beginPath();
            context.roundRect(boxX, boxY, boxWidth, boxHeight, 8);
            context.fill();
            context.stroke();
            context.shadowBlur = 0;

            // Article name (bold title row)
            context.font = '600 11px sans-serif';
            context.fillStyle = panelTheme?.textPrimary ?? '#1f2937';
            context.textAlign = 'left';
            context.textBaseline = 'top';
            context.fillText(info.articleName, boxX + boxPaddingX, boxY + boxPaddingY);

            // Separator line
            const sepY = boxY + boxPaddingY + titleHeight - 1;
            context.strokeStyle = panelTheme?.panelBorder ?? 'rgba(24, 36, 58, 0.25)';
            context.lineWidth = 1;
            context.beginPath();
            context.moveTo(boxX + boxPaddingX, sepY);
            context.lineTo(boxX + boxWidth - boxPaddingX, sepY);
            context.stroke();

            const contentY = sepY + 5;

            // Thumbnail
            if (hasThumb && thumbnailImg) {
                const thumbX = boxX + boxWidth - boxPaddingX - thumbSize;
                const thumbY = contentY;
                context.save();
                context.beginPath();
                context.roundRect(thumbX, thumbY, thumbSize, thumbSize, 4);
                context.clip();
                context.drawImage(thumbnailImg, thumbX, thumbY, thumbSize, thumbSize);
                context.restore();
            }

            // Metric lines
            context.font = '500 11px sans-serif';
            context.fillStyle = panelTheme?.textPrimary ?? '#1f2937';
            context.textAlign = 'left';
            context.textBaseline = 'top';
            lines.forEach((line, index) => {
                context.fillText(line, boxX + boxPaddingX, contentY + index * lineHeight);
            });
            context.restore();
        } else {
            lastHoveredInstanceIdRef.current = null;
        }
    });

    return null;
};
